/**
 * Currency Utility Functions - Fix for floating point precision issues
 * All currency calculations should use these functions to ensure precision
 */

export class CurrencyUtils {
  /**
   * Convert string/number to precise number with 2 decimal places
   */
  static toPreciseNumber(value: string | number): number {
    const num = typeof value === 'string' ? parseFloat(value) : value
    if (isNaN(num)) return 0
    return Math.round((num + Number.EPSILON) * 100) / 100
  }

  /**
   * Add two currency values with precision
   */
  static add(a: string | number, b: string | number): number {
    const numA = CurrencyUtils.toPreciseNumber(a)
    const numB = CurrencyUtils.toPreciseNumber(b)
    return Math.round((numA + numB + Number.EPSILON) * 100) / 100
  }

  /**
   * Subtract two currency values with precision
   */
  static subtract(a: string | number, b: string | number): number {
    const numA = CurrencyUtils.toPreciseNumber(a)
    const numB = CurrencyUtils.toPreciseNumber(b)
    return Math.round((numA - numB + Number.EPSILON) * 100) / 100
  }

  /**
   * Multiply currency value with precision
   */
  static multiply(a: string | number, b: string | number): number {
    const numA = CurrencyUtils.toPreciseNumber(a)
    const numB = CurrencyUtils.toPreciseNumber(b)
    return Math.round((numA * numB + Number.EPSILON) * 100) / 100
  }

  /**
   * Sum an array of currency values with precision
   */
  static sum(values: (string | number)[]): number {
    let total = 0
    for (const value of values) {
      total = CurrencyUtils.add(total, value)
    }
    return total
  }

  /**
   * Format currency for display (2 decimal places)
   */
  static format(value: string | number): string {
    return CurrencyUtils.toPreciseNumber(value).toFixed(2)
  }

  /**
   * Compare two currency values with precision (returns true if a >= b)
   */
  static isGreaterOrEqual(a: string | number, b: string | number): boolean {
    const numA = CurrencyUtils.toPreciseNumber(a)
    const numB = CurrencyUtils.toPreciseNumber(b)
    return numA >= numB
  }

  /**
   * Compare two currency values with precision (returns true if a < b)
   */
  static isLess(a: string | number, b: string | number): boolean {
    const numA = CurrencyUtils.toPreciseNumber(a)
    const numB = CurrencyUtils.toPreciseNumber(b)
    return numA < numB
  }

  /**
   * Compare two currency values with precision (returns true if a > b)
   */
  static isGreater(a: string | number, b: string | number): boolean {
    const numA = CurrencyUtils.toPreciseNumber(a)
    const numB = CurrencyUtils.toPreciseNumber(b)
    return numA > numB
  }

  /**
   * Check if two currency values are equal with precision
   */
  static isEqual(a: string | number, b: string | number): boolean {
    const numA = CurrencyUtils.toPreciseNumber(a)
    const numB = CurrencyUtils.toPreciseNumber(b)
    return numA === numB
  }
}
